import {
  APIProvider,
  InfoWindow,
  Map,
  MapCameraChangedEvent,
  MapCameraProps,
  Marker,
} from "@vis.gl/react-google-maps";
import { Eye, Pencil } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { CONFIG } from "../../constants/config";
import { PAGES } from "../../constants/pages";
import strings from "../../i18n/i18nStrings";
import { TLocation } from "../../types/Locations";
import { TDevice } from "../../types/Devices";
import dayjs from "dayjs";
import LocCircle from "../nameCircle/LocCircle";


const LocationCard = ({
  location,
  index,
  onEditLocation
}: {
  location: TLocation;
  index: number;
  onEditLocation: () => void;
}) => {
  const checkDeviceIssues = (devices: TDevice[]): number => {
    return devices.reduce((count, device) => {
      // Tank status issues
      if (device.tank_disconnected === true || device.tank_unsealed === true) {
        count++;
      }
      
      // Low battery (below 20%)
      if (device.battery_percentage !== undefined && device.battery_percentage < 20) {
        count++;
      }
      
      // Device offline for more than 24 hours
      if (device.last_event_updated_at) {
        const hoursSinceUpdate = dayjs().diff(device.last_event_updated_at, 'hour');
        if (hoursSinceUpdate >= 24) {
          count++;
        }
      }
      
      // Device never connected after creation
      if (!device.last_event_updated_at && device.created_at) {
        const timeSinceCreation = dayjs().diff(device.created_at, 'minute');
        if (timeSinceCreation >= CONFIG.MINUTES_FOR_DEVICE_TO_CALL_IN) {
          count++;
        }
      }
      
      return count;
    }, 0);
  };

  const issues = checkDeviceIssues(location.devices || []);
  const {
    name,
    devices,
  } = location;
  const [selectedMarker, setSelectedMarker] = useState<string | null>(null);
  const hasValidCoordinates = location.lat && location.lon && !isNaN(location.lat) && !isNaN(location.lon);
  
  const [cameraProps, setCameraProps] = useState<MapCameraProps>({
    center: hasValidCoordinates ? {
      lat: Number(location.lat),
      lng: Number(location.lon)
    } : { lat: 0, lng: 0 },
    zoom: 18
  })

  useEffect(() => {
    if (hasValidCoordinates) {
      setCameraProps(prev => ({
        ...prev,
        center: {
          lat: Number(location.lat),
          lng: Number(location.lon)
        },
      }))
    }
  }, [location.lat, location.lon, hasValidCoordinates])

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="flex items-start flex-1 min-w-0">
          <div className="flex-shrink-0">
            <LocCircle name={name} index={index} color={location.color || "#3B82F6"}/>
          </div>
          <h3 className="ml-4 text-xl font-bold leading-tight line-clamp-2 break-words flex-1">
            {name}
          </h3>
        </div>
        <div className="flex-shrink-0">
          <button
            className="px-4 py-3 border border-blue-700 rounded-md shadow-sm text-xs font-bold uppercase tracking-wider text-blue-700 hover:bg-blue-700 hover:text-white"
            onClick={onEditLocation}
          >
            <Pencil className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex justify-between text-sm font-semibold text-gray-900 mb-4">
        {devices?.length ? (
          <span>
            {devices.length === 1
              ? strings.locations.oneDevice
              : strings.locations.multiDevices(devices.length)}
          </span>
        ) : (
          <a
            href={PAGES.DEVICES}
            className="text-sm font-semibold underline text-blue-700 hover:text-blue-800 flex items-center"
          >
            {strings.locations.addDevices}
          </a>
        )}
        <span className={issues ? "text-red-700" : "text-gray-500"}>
          {issues
            ? strings.locations.issues(issues)
            : strings.locations.noIssues}
        </span>
      </div>
      <div className="flex items-center justify-between mb-3 h-64 bg-gray-200 rounded-md overflow-hidden">
        {hasValidCoordinates ? (
          <APIProvider apiKey={CONFIG.GOOGLE_API_KEY || ""}>
            <Map
              style={{ width: "100vw", height: "100vh" }}
              gestureHandling={"greedy"}
              disableDefaultUI={true}
              onCameraChanged={(ev: MapCameraChangedEvent) => setCameraProps(ev.detail)}
              {...cameraProps}
            >
              {/* Show location marker if no devices */}
              {(!location.devices || location.devices.length === 0) && (
                <Marker
                  position={{
                    lat: Number(location.lat),
                    lng: Number(location.lon),
                  }}
                />
              )}
              
              {/* Show device markers */}
              {location.devices && location.devices.length > 0 && location.devices.map((device) => {
                // Check if device has valid coordinates
                const deviceLat = device.lat !== null && device.lat !== undefined ? Number(device.lat) : null;
                const deviceLon = device.lon !== null && device.lon !== undefined ? Number(device.lon) : null;
                const deviceHasCoordinates = deviceLat !== null && deviceLon !== null && !isNaN(deviceLat) && !isNaN(deviceLon);
                
                // Use device coordinates if available, otherwise use location coordinates
                const markerLat = deviceHasCoordinates ? deviceLat : Number(location.lat);
                const markerLng = deviceHasCoordinates ? deviceLon : Number(location.lon);
                
                return (
                  <Fragment key={device.device_id}>
                    <Marker
                      position={{
                        lat: markerLat,
                        lng: markerLng,
                      }}
                      onClick={() => setSelectedMarker(device.device_id)}
                    />

                    {selectedMarker === device.device_id && (
                      <InfoWindow
                        position={{
                          lat: markerLat,
                          lng: markerLng,
                        }}
                        onCloseClick={() => setSelectedMarker(null)}
                      >
                        <div>
                          <h2 className="text-lg font-bold mb-2">
                            {device.name}
                          </h2>
                          <ul className="text-sm">
                            <li>
                              Device ID: {device.device_id}
                            </li>
                            <li>
                              Coordinates: {deviceHasCoordinates ? `${deviceLat}, ${deviceLon}` : 'Using location coordinates'}
                            </li>
                            <li>
                              Last Updated:{" "}
                              {device.last_event_updated_at 
                                ? new Date(device.last_event_updated_at).toLocaleDateString()
                                : "Never"}
                            </li>
                            {device.battery_percentage !== undefined && (
                              <li>Battery: {device.battery_percentage}%</li>
                            )}
                          </ul>
                        </div>
                      </InfoWindow>
                    )}
                  </Fragment>
                );
              })}
            </Map>
          </APIProvider>
        ) : (
          <div className="text-sm font-semibold text-gray-900 text-center w-full flex items-center justify-center h-full">
            {strings.locations.noLocationData}
          </div>
        )}
      </div>
      {devices?.length ? (
        <a
          href={`${PAGES.DASHBOARD}?location=${location?._id}`}
          className="text-sm font-semibold underline text-blue-700 hover:text-blue-800 flex items-center"
        >
          <Eye className="w-4 h-4 mr-1" />
          {strings.locations.viewDashboard}
        </a>
      ) : (
        <span className="text-sm font-semibold underline cursor-not-allowed flex items-center">
          <Eye className="w-4 h-4 mr-1" />
          {strings.locations.viewDashboard}
        </span>
      )}
    </div>
  );
};
export default LocationCard;
